import React, { FC } from 'react';
import { SidebarHeading } from '../../SidebarHeading';
import { useReferencesContext } from '../ReferencesContext';
import { LinkOff } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { JSONContent } from '@tiptap/core';
import { StyledHeader } from '../styles';
import { StyledLink } from '../../../../../Components';
import { capitalizeFirstLetter } from '../../../../../utils/string';
import { getLongTypeDescription, NumberedNodeType } from '../../../tiptap/numbering';
import { useGuideCmsContext } from '../../../GuideCmsContext';

export const BrokenReferences: FC = () => {
  const { brokenReferences, hasBrokenReferences } = useReferencesContext();
  if (!hasBrokenReferences) {
    return <Alert severity="success">Dokumentet inneholder ingen brutte referanser.</Alert>;
  }
  return (
    <div>
      <SidebarHeading icon={<LinkOff />}>Brutte referanser</SidebarHeading>
      <Alert severity="warning">Dokumentet inneholder brutte referanser som ikke lenger peker på noe.</Alert>
      <InternalReferencesSection nodes={brokenReferences.internalHeadingLinks} heading="Punkter" />
      <InternalReferencesSection nodes={brokenReferences.internalTableLinks} heading="Tabeller" />
      <InternalReferencesSection nodes={brokenReferences.internalFigureLinks} heading="Figurer" />
      <InternalReferencesSection nodes={brokenReferences.internalAppLinks} heading="Apper" />
      <CitationReferencesSection nodes={brokenReferences.citationLinks} />
    </div>
  );
};

const CitationReferencesSection: FC<{ nodes: JSONContent[] }> = ({ nodes }) => {
  if (nodes.length === 0) return null;
  return (
    <>
      <StyledHeader>Litteraturhenvisninger</StyledHeader>
      {nodes.map((node, index) => (
        <CitationBrokenLink key={index} node={node} />
      ))}
    </>
  );
};

const CitationBrokenLink: FC<{ node: JSONContent }> = ({ node }) => {
  const { editor } = useGuideCmsContext();

  const handleOnBrokenReferenceClick = () => {
    if (!editor) return;
    const { doc } = editor.state;
    let foundPos: number | null = null;
    doc.descendants((desNode, pos) => {
      if (foundPos !== null) return false;
      if (desNode.type.name === 'citationLink' && desNode.attrs.citationId === node.attrs?.citationId) {
        foundPos = pos;
        return false;
      }
    });
    if (foundPos === null) return;
    editor.chain().focus().setTextSelection(foundPos).scrollIntoViewWithOffset().run();
  };

  return (
    <div>
      <StyledLink to="" onClick={(e) => { e.preventDefault(); handleOnBrokenReferenceClick(); }}>
        Litteraturhenvisning slettet
      </StyledLink>
    </div>
  );
};

const InternalReferencesSection: FC<{ nodes: JSONContent[]; heading: string }> = ({ nodes, heading }) => {
  if (nodes.length === 0) return null;
  return (
    <>
      <StyledHeader>{heading}</StyledHeader>
      {nodes.map((node, index) => (
        <InternalBrokenLink key={index} node={node} />
      ))}
    </>
  );
};

export const InternalBrokenLink: FC<{ node: JSONContent }> = ({ node }) => {
  const { editor } = useGuideCmsContext();

  const handleOnBrokenReferenceClick = () => {
    const nodePos = findPositionOfNode();

    if (!nodePos) return;
    editor?.chain().focus().setTextSelection(nodePos).scrollIntoViewWithOffset().run();
  };

  function findPositionOfNode(): number | null {
    if (!editor) return null;

    const { doc } = editor.state;
    let foundPos: number | null = null;

    doc.descendants((desNode, pos) => {
      // If we already found it, stop searching
      if (foundPos !== null) return false;

      if (desNode.type.name === 'internalLink' && desNode.attrs.id === node.attrs?.id) {
        foundPos = pos;
        return false; // Stop traversal
      }
    });

    return foundPos;
  }
  return (
    <div>
      <StyledLink to="" onClick={(e) => { e.preventDefault(); handleOnBrokenReferenceClick(); }}>
        {capitalizeFirstLetter(getLongTypeDescription(node.attrs?.type as NumberedNodeType, node.attrs?.format))} undefined
      </StyledLink>
    </div>
  );
};
